import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { fetchData } from '../../../../helpers/methods';
import { formatDateForAgo } from '../../../../helpers/date-time';

import CommentInput from './comment-input';
/* eslint-disable-next-line  import/no-self-import */
import CommentReply from './comment';

import SpinnerContainer from '../../../../component-library/spinner-container';

const Comment = ({
	comment,
	id,
	canReply,
	maxCommentLength,
}) => {
	const [loadingReplies, setLoadingReplies] = useState(true);
	const [repliesData, setRepliesData] = useState({});
	const [branchesData, setBranchesData] = useState(comment.branches || []);
	const [isDeleted, setIsDeleted] = useState(false);
	const [isEditable, setIsEditable] = useState(false);
	const [showReplyField, setShowReplyField] = useState(false);
	const [showReplies, setShowReplies] = useState(false);
	const [newComment, setNewComment] = useState(comment.message || '');
	const [newCommentError, setNewCommentError] = useState('');
	const [newReply, setNewReply] = useState('');
	const [newReplyError, setNewReplyError] = useState('');

	const getReplies = newBranchesData => {
		setShowReplies(true);
		setLoadingReplies(true);
		const urlArray = [];
		const newReplyData = newBranchesData || branchesData;
		if (newReplyData.length > 0) {
			newReplyData.forEach(entry => {
				urlArray.push(`/api/web/comments/catitem/${id}/conv/${entry}?pageSize=1000`);
			});
			Promise.all(urlArray.map(url => fetchData({
				uri: url,
				method: 'GET',
			}).then(({ ok, body }) => {
				if (ok) {
					return body;
				}
				return 'FAIL';
			})))
				.then(data => {
					if (!data.includes('FAIL')) {
						setRepliesData(data);
						setLoadingReplies(false);
					} else {
						setShowReplies(false);
						setLoadingReplies(false);
					}
				})
				.catch(() => {
					setShowReplies(false);
					setLoadingReplies(false);
				});
		} else {
			setShowReplies(false);
			setLoadingReplies(false);
		}
	};

	const handleEditComment = () => {
		const commentData = {
			message: newComment,
			key: comment.key,
			order: comment.order,
		};
		fetchData({
			uri: `/api/web/comments/catitem/${id}`,
			method: 'PUT',
			options: {
				body: JSON.stringify(commentData),
			},
		})
			.then(({ ok, body }) => {
				if (ok) {
					setNewComment(body.message);
					setNewCommentError('');
					setIsEditable(false);
				} else {
					setNewCommentError('Sorry, your comment could not be edited at this time, please try again.');
				}
			})
			.catch(() => {
				setNewCommentError('Sorry, your comment could not be edited at this time, please try again.');
			});
	};

	const handleDeleteComment = () => {
		const commentData = {
			deleted: true,
			key: comment.key,
			order: comment.order,
		};
		fetchData({
			uri: `/api/web/comments/catitem/${id}`,
			method: 'PUT',
			options: {
				body: JSON.stringify(commentData),
			},
		})
			.then(({ ok, body }) => {
				if (ok) {
					setIsDeleted(true);
					setBranchesData([]);
					setShowReplies(false);
					setLoadingReplies(false);
					setNewComment(body.message);
				}
			});
	};

	const handleReply = () => {
		const conversationId = comment.key;
		const { order } = comment;
		const replyData = {
			message: newReply,
		};
		fetchData({
			uri: `/api/web/comments/catitem/${id}/conv/${conversationId}/order/${order}/reply`,
			method: 'POST',
			options: {
				body: JSON.stringify(replyData),
			},
		})
			.then(({ ok, body }) => {
				if (ok) {
					const newBranchKey = body.key;
					const newBranchesData = branchesData;
					newBranchesData.push(newBranchKey);
					setBranchesData(newBranchesData);
					setNewReply('');
					setNewReplyError('');
					setShowReplyField(false);
					getReplies(newBranchesData);
				} else {
					setNewReplyError('Sorry, your reply could not be posted at this time, please try again.');
				}
			})
			.catch(() => {
				setNewReplyError('Sorry, your reply could not be posted at this time, please try again.');
			});
	};

	const handleEditCommentValidation = () => {
		setNewCommentError('');
		if (newComment !== '') {
			if (newComment.length > maxCommentLength) {
				setNewCommentError(`Comments must be less than ${maxCommentLength} characters in length.`);
			} else {
				handleEditComment();
			}
		} else {
			setNewCommentError('You must enter a comment.');
		}
	};

	const handleReplyValidation = () => {
		setNewReplyError('');
		if (newReply !== '') {
			if (newReply.length > maxCommentLength) {
				setNewReplyError(`Replies must be less than ${maxCommentLength} characters in length.`);
			} else {
				handleReply();
			}
		} else {
			setNewReplyError('You must enter a reply.');
		}
	};

	return (
		<div className="comment__container">
			<div className="comment__inner-container">
				<div className="flex-row justify-space-between">
					<div className="comment__name">{comment.owner}</div>
					<div className="grey-text">{formatDateForAgo(comment.timestamp)}</div>
				</div>
				<div className="comment__message">
					{isEditable && (
						<CommentInput
							placeholder="Add a comment..."
							formAction={value => setNewComment(value)}
							formValue={newComment}
							formError={newCommentError}
							buttonTitle="Post Comment"
							buttonAction={() => handleEditCommentValidation()}
							maxCommentLength={maxCommentLength}
						/>
					)}
					{!isEditable && (
						(newComment && newComment !== 'DELETED') ? (
							newComment
						) : (
							<span className="grey-text">Message has been deleted.</span>
						)
					)}
				</div>
			</div>
			<div className="comment__options">
				{comment.mine && (
					<>
						<button
							type="button"
							onClick={() => setIsEditable(!isEditable)}
						>
							Edit
						</button>
						<div className="comment__options-divider">&bull;</div>
						<button
							type="button"
							onClick={() => handleDeleteComment()}
						>
							Delete
						</button>
						{(canReply && comment.deleted !== true && !isDeleted) && (
							<div className="comment__options-divider">&bull;</div>
						)}
					</>
				)}
				{(canReply && comment.deleted !== true && !isDeleted) && (
					<>
						<button
							type="button"
							onClick={() => setShowReplyField(!showReplyField)}
						>
							Reply
						</button>
					</>
				)}
				{comment.branches?.length > 0 && !isDeleted && (
					<>
						<div className="comment__options-divider">&bull;</div>
						{showReplies ? (
							<button
								type="button"
								onClick={() => setShowReplies(false)}
							>
								Hide Replies
							</button>
						) : (
							<button
								type="button"
								onClick={() => getReplies()}
							>
								{`${comment.branches.length} ${comment.branches.length === 1 ? 'Reply' : 'Replies'}`}
							</button>
						)}
					</>
				)}
			</div>
			{showReplyField && (
				<div className="comment__replies">
					<CommentInput
						placeholder="Add a reply..."
						formAction={value => setNewReply(value)}
						formValue={newReply}
						formError={newReplyError}
						buttonTitle="Post Reply"
						buttonAction={() => handleReplyValidation()}
						maxCommentLength={maxCommentLength}
					/>
				</div>
			)}
			{showReplies && (
				<div className="comment__replies">
					{loadingReplies && (
						<SpinnerContainer padding={false} />
					)}
					{!loadingReplies && (
						repliesData?.length > 0 && (
							repliesData.map(conversation => (
								conversation.conversations[0].comments.length > 0 && (
									conversation.conversations[0].comments.map((item, index) => (
										<CommentReply
											// eslint-disable-next-line react/no-array-index-key
											key={index}
											id={id}
											comment={item}
											canReply={conversation.conversations[0].conversation.canReply}
											maxCommentLength={maxCommentLength}
										/>
									))
								)
							))
						)
					)}
				</div>
			)}
		</div>
	);
};

export default Comment;

Comment.propTypes = {
	comment: PropTypes.shape({
		branches: PropTypes.arrayOf(
			PropTypes.string,
		),
		owner: PropTypes.string.isRequired,
		message: PropTypes.string,
		timestamp: PropTypes.number.isRequired,
		key: PropTypes.string.isRequired,
		order: PropTypes.number.isRequired,
		mine: PropTypes.bool,
		deleted: PropTypes.bool,
	}).isRequired,
	id: PropTypes.string.isRequired,
	canReply: PropTypes.bool.isRequired,
	maxCommentLength: PropTypes.number.isRequired,
};
